import { FooterVariant } from "@skylead/component-library"
import classNames from "classnames"
import React from "react"

// eslint-disable-next-line import/no-cycle
import AccountLabelAdditionalContent from "../components/atoms/AccountLabelAdditionalContent"
import { ButtonVariant } from "../components/atoms/Button"
// eslint-disable-next-line import/no-cycle
import { ModalVariant } from "../components/atoms/Modal"
import * as actions from "../redux/actions"
import { store } from "../redux/store"
import {
  clearModalReduxFields,
  connectionProcessModal,
  getLabelModalAction,
  loginWithStream,
  verifyPin,
  verifyTwoFactorAuth,
} from "../utils/account-utils"
import { MODAL_HEADER_VARIANT } from "./modal-constants"

export const LABEL_COLORS = {
  RED: [
    "UNKNOWN",
    "WRONG_AUTH",
    "PAYMENT_REQUIRED",
    "CANCELED",
    "RESTRICTED",
    "INVALID_SUBSCRIPTION",
    "PIN_WRONG",
    "LOGIN_FAILED",
    "VERIFY_IDENTITY",
    "TWO_FACTOR_AUTH_WRONG",
    "TWO_FACTOR_AUTH_ERROR",
    "SET_UP_TWO_FACTOR_AUTH",
  ],
  YELLOW: ["PIN_REQUESTED", "CAPTCHA_REQUESTED", "REGISTER_PHONE", "TWO_FACTOR_AUTH", "OFFLINE"],
  BLUE: [
    "PIN_VERIFYING",
    "CREATED",
    "CHECKING_CONNECTIVITY",
    "LOGGING_LINKEDIN",
    "SCHEDULED_EXECUTION",
    "INITIALIZING_CONNECTION",
    "TWO_FACTOR_AUTH_QUEUED",
    "TWO_FACTOR_AUTH_VERIFYING",
  ],
  GREEN: ["AUTH_OK"],
}

// QQQ-Not-Used
export const LABEL_DESCRIPTION = {
  UNKNOWN: "Unknown error",
  CREATED: "Checking authentication",
  WRONG_AUTH: "Wrong credentials",
  PIN_REQUESTED: "PIN code required",
  CAPTCHA_REQUESTED: "CAPTCHA required",
  AUTH_OK: "Connected",
  OFFLINE: "Outside work hours",
  PAYMENT_REQUIRED: "Payment needed",
  CANCELED: "Canceled",
  RESTRICTED: "Access denied",
  TWO_FACTOR_AUTH: "2FA code required",
  INVALID_SUBSCRIPTION: "Invalid LinkedIn subscription",
  PIN_VERIFYING: "Verifying PIN code",
  PIN_WRONG: "Wrong PIN code",
  CONNECT: "Connect",
  SCHEDULED_EXECUTION: "Scheduled execution",
  INITIALIZING_CONNECTION: "Initializing connection",
  CHECKING_CONNECTIVITY: "Checking connectivity",
  LOGGING_LINKEDIN: "Logging on LinkedIn",
  TWO_FACTOR_AUTH_WRONG: "Wrong 2FA code",
  TWO_FACTOR_AUTH_VERIFYING: "Verifying 2FA code",
  TWO_FACTOR_AUTH_QUEUED: "2FA code queued",
  TWO_FACTOR_AUTH_ERROR: "2FA error",
  LOGIN_FAILED: "Proxy error",
  VERIFY_IDENTITY: "Verify identity",
  SET_UP_TWO_FACTOR_AUTH: "Set up 2FA",
  REGISTER_PHONE: "Phone number required",
}

// Support working hours in Belgrade time zone (Europe/Belgrade)
// Weekdays: 8:00 - 01:00 next day (i.e., 8am to 1am, so 8:00-24:00 and 0:00-1:00)
// Weekends: 11:00 - 19:00

const SUPPORT_WORKING_TIME = {
  WEEKDAYS: { START: 8, END: 1 }, // 8:00 to 01:00 next day
  WEEKENDS: { START: 11, END: 19 }, // 11:00 to 19:00
}

export const isSupportWorkingHours = () => {
  const now = new Date()

  // Get current time in Europe/Belgrade time zone
  // Use toLocaleString to get the local time in Belgrade, then parse it
  const belgradeTimeString = now.toLocaleString("en-US", { timeZone: "Europe/Belgrade" })
  const belgradeDate = new Date(belgradeTimeString)
  const day = belgradeDate.getDay() // 0 = Sunday, 6 = Saturday
  const hour = belgradeDate.getHours()

  const isWeekend = day === 0 || day === 6

  const { START, END } = isWeekend ? SUPPORT_WORKING_TIME.WEEKENDS : SUPPORT_WORKING_TIME.WEEKDAYS

  let isSupportWorking = false

  if (!isWeekend) {
    // Weekdays: 8:00 - 01:00 next day
    // This means from 8:00 to 23:59, and also from 0:00 to 0:59 (hour === 0)
    if ((hour >= START && hour < 24) || (hour >= 0 && hour < END)) {
      isSupportWorking = true
    }
  } else {
    // Weekends: 11:00 - 19:00
    isSupportWorking = hour >= START && hour < END
  }

  return isSupportWorking
}

const isSupportWorking = isSupportWorkingHours()

export const MODAL_HEADER_TITLE = tt => ({
  UNKNOWN: tt("unknown-error"),
  CREATED: tt("connecting-linkedin-account"),
  WRONG_AUTH: tt("wrong-credentials"),
  PIN_REQUESTED: tt("pin-code-required"),
  CAPTCHA_REQUESTED: isSupportWorking ? tt("solving-captcha-support") : tt("captcha-required"),
  AUTH_OK: tt("connected"),
  OFFLINE: tt("outside-work-hours"),
  PAYMENT_REQUIRED: tt("payment-needed"),
  CANCELED: tt("canceled"),
  RESTRICTED: tt("access-denied"),
  TWO_FACTOR_AUTH: tt("two-factor-code-required"),
  INVALID_SUBSCRIPTION: tt("invalid-linkedin-subscription"),
  PIN_VERIFYING: tt("verifying-pin-code"),
  PIN_WRONG: tt("wrong-pin-code"),
  CONNECT: tt("connect"),
  SCHEDULED_EXECUTION: tt("connecting-linkedin-account"),
  INITIALIZING_CONNECTION: tt("initializing-connection"),
  CHECKING_CONNECTIVITY: tt("checking-connectivity"),
  LOGGING_LINKEDIN: tt("logging-on-linkedin"),
  TWO_FACTOR_AUTH_WRONG: tt("wrong-two-factor-code"),
  TWO_FACTOR_AUTH_VERIFYING: tt("verifying-two-factor-code"),
  TWO_FACTOR_AUTH_QUEUED: tt("verifying-two-factor-code"),
  TWO_FACTOR_AUTH_ERROR: tt("wrong-two-factor-code"),
  LOGIN_FAILED: tt("proxy-error"),
  VERIFY_IDENTITY: tt("verify-identity"),
  SET_UP_TWO_FACTOR_AUTH: tt("set-two-factor"),
  REGISTER_PHONE: tt("phone-number-required"),
})

export const LABEL_HEADER = {
  BLUE: [
    "CREATED",
    "SCHEDULED_EXECUTION",
    "PIN_VERIFYING",
    "TWO_FACTOR_AUTH_VERIFYING",
    "INITIALIZING_CONNECTION",
    "CHECKING_CONNECTIVITY",
    "LOGGING_LINKEDIN",
    "TWO_FACTOR_AUTH_QUEUED",
  ],
  RED: [
    "WRONG_AUTH",
    "LOGIN_FAILED",
    "CANCELED",
    "RESTRICTED",
    "INVALID_SUBSCRIPTION",
    "TWO_FACTOR_AUTH_WRONG",
    "VERIFY_IDENTITY",
    "SET_UP_TWO_FACTOR_AUTH",
    "UNKNOWN",
    "PAYMENT_REQUIRED",
  ],
  GREEN: ["PIN_REQUESTED", "CAPTCHA_REQUESTED", "OFFLINE", "REGISTER_PHONE0", "TWO_FACTOR_AUTH"],
  ORANGE: ["AUTH_OK"],
}

export const ACCOUNT_SELECT_OPTIONS = [
  { value: "1" },
  { value: "2" },
  { value: "3" },
  { value: "4" },
  { value: "5" },
]

export const BASIC_LABEL_NAMES = ["UNKNOWN", "CREATED", "CAPTCHA_REQUESTED", "AUTH_OK", "OFFLINE"]

export const ACTIVE_STATUS_IDS = [5]

export const labelStatusModal = async ({
  tt,
  person,
  statusName,
  userMessage,
  accountStatuses,
}) => {
  const accountStatusLabelClass = classNames({
    [MODAL_HEADER_VARIANT.BLUE]: LABEL_HEADER.BLUE.includes(statusName),
    [MODAL_HEADER_VARIANT.RED]: LABEL_HEADER.RED.includes(statusName),
    [MODAL_HEADER_VARIANT.ORANGE]: LABEL_HEADER.ORANGE.includes(statusName),
    [MODAL_HEADER_VARIANT.GREEN]: LABEL_HEADER.GREEN.includes(statusName),
  })

  const modalAction = status => {
    const callback = getLabelModalAction(tt, status, person)
    if (callback) {
      return async e => {
        const isVerified = await callback(e)
        if (isVerified) {
          connectionProcessModal()
        }
      }
    }

    return undefined
  }

  const onSuccessAction = BASIC_LABEL_NAMES.includes(statusName)
    ? undefined
    : modalAction(statusName)

  let footerVariant = FooterVariant.ACKNOWLEDGMENT
  let primaryBtn = ["OFFLINE", "RESTRICTED", "CANCELED"].includes(statusName)
    ? { label: tt("close") }
    : { label: tt("cancel") }

  let secondaryBtn

  if (onSuccessAction) {
    footerVariant = FooterVariant.TRANSACTIONAL
    primaryBtn = {
      label: statusName === "CANCELED" ? tt("reactivate") : tt("submit"),
      onClick: () => onSuccessAction(),
    }
    secondaryBtn = { label: tt("cancel") }
  }

  const noHideOnSubmit = !!secondaryBtn && !["CANCELED"].includes(statusName)

  store.dispatch(
    actions.showModal(
      ModalVariant.SMALL,
      MODAL_HEADER_TITLE(tt)[statusName],
      <>
        <AccountLabelAdditionalContent
          person={person}
          statusName={statusName}
          userMessage={userMessage}
          accountStatuses={accountStatuses}
        />
      </>,
      footerVariant,
      primaryBtn,
      secondaryBtn,
      undefined,
      noHideOnSubmit,
      undefined,
      undefined,
      accountStatusLabelClass,
    ),
  )
}

export const LINKEDIN_ACCOUNT_GLOBAL_STATUS = {
  UNKNOWN: 0, // old modal
  CREATED: 1,
  WRONG_AUTH: 2,
  PIN_REQUESTED: 3,
  PIN_VERIFYING: 12,
  PIN_WRONG: 13, //
  CAPTCHA_REQUESTED: 4,
  AUTH_OK: 5,
  OFFLINE: 6,
  CANCELED: 8,
  RESTRICTED: 9, // old modal
  INVALID_SUBSCRIPTION: 11, // old modal
  SCHEDULED_EXECUTION: 14,
  INITIALIZING_CONNECTION: 15,
  CHECKING_CONNECTIVITY: 16,
  LOGGING_LINKEDIN: 17,
  TWO_FACTOR_AUTH: 10,
  TWO_FACTOR_AUTH_WRONG: 18,
  TWO_FACTOR_AUTH_VERIFYING: 19,
  TWO_FACTOR_AUTH_QUEUED: 20,
  TWO_FACTOR_AUTH_ERROR: 21,
  // custom statuses
  LOGIN_FAILED: 22,
  VERIFY_IDENTITY: 23,
  SET_UP_TWO_FACTOR_AUTH: 24,
  TIMES_UP_CUSTOM_STATUS: 101,
  RESEND_PIN_CUSTOM_STATUS: 102,
  CAPTCHA_FAILED: 103,
  REGISTER_PHONE: 25,
}
export const getLinkedinGlobalStatus = (tt, id) => {
  const linkedinAccountGlobalStatus = new Map()

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.CREATED, {
    percentage: 14,
    image: "chart-dots-3-80",
    text: tt("linkedin.global.status.created.msg"),
    button: false,
    headerClass: MODAL_HEADER_VARIANT.BLUE,
    footerVariant: FooterVariant.ACKNOWLEDGMENT,
    primaryBtn: {
      label: tt("close-work-background"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.SCHEDULED_EXECUTION, {
    percentage: 14,
    image: "chart-dots-3-80",
    text: tt("linkedin.global.status.schedule-execution.msg"),
    button: false,
    headerClass: MODAL_HEADER_VARIANT.BLUE,
    footerVariant: FooterVariant.ACKNOWLEDGMENT,
    primaryBtn: {
      label: tt("close-work-background"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.INITIALIZING_CONNECTION, {
    percentage: 27,
    image: "chart-dots-3-80",
    text: tt("linkedin.global.status.initializing-connection.msg"),
    button: false,
    headerClass: MODAL_HEADER_VARIANT.BLUE,
    footerVariant: FooterVariant.ACKNOWLEDGMENT,
    primaryBtn: {
      label: tt("close-work-background"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.CHECKING_CONNECTIVITY, {
    percentage: 52,
    image: "chart-dots-3-80",
    text: tt("linkedin.global.status.check-connection.msg"),
    button: false,
    headerClass: MODAL_HEADER_VARIANT.BLUE,
    footerVariant: FooterVariant.ACKNOWLEDGMENT,
    primaryBtn: {
      label: tt("close-work-background"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.LOGGING_LINKEDIN, {
    percentage: 75,
    image: "chart-dots-3-80",
    text: tt("linkedin.global.status.login-linkedin.msg"),
    button: false,
    headerClass: MODAL_HEADER_VARIANT.BLUE,
    footerVariant: FooterVariant.ACKNOWLEDGMENT,
    primaryBtn: {
      label: tt("close-work-background"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.AUTH_OK, {
    percentage: 100,
    image: "check-80",
    text: tt("linkedin.global.status.auth-ok.msg"),
    progressBar: LINKEDIN_ACCOUNT_GLOBAL_STATUS.AUTH_OK,
    headerClass: MODAL_HEADER_VARIANT.ORANGE,
    footerVariant: FooterVariant.ACKNOWLEDGMENT,
    primaryBtn: {
      label: tt("close"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.CAPTCHA_REQUESTED, {
    percentage: 75,
    image: "captcha-80",
    text: isSupportWorking
      ? tt("linkedin.global.status.solving-captcha-support.msg")
      : `${tt("linkedin.global.status.captcha-request.msg1")} ${tt(
          "linkedin.global.status.captcha-request.msg2",
        )} ${tt("linkedin.global.status.captcha-request.msg3")}`,
    primaryBtn: isSupportWorking
      ? {
          label: tt("close"),
          onClick: () => {
            clearModalReduxFields()
            store.dispatch(actions.closeModal())
          },
        }
      : {
          field: "captcha",
          onClick: () => {
            clearModalReduxFields()
            loginWithStream(tt)
          },
          label: tt("resolve-on-linkedin"),
        },
    secondaryBtn: isSupportWorking
      ? undefined
      : {
          label: tt("close"),
          onClick: () => {
            clearModalReduxFields()
            store.dispatch(actions.closeModal())
          },
        },
    inputFiled: false,
    noHideOnSubmit: true,
    headerClass: MODAL_HEADER_VARIANT.GREEN,
    footerVariant: isSupportWorking ? FooterVariant.ACKNOWLEDGMENT : FooterVariant.TRANSACTIONAL,
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.PIN_REQUESTED, {
    percentage: 75,
    image: "password-user-80",
    timer: true,
    text: tt("linkedin.global.status.pin-request.msg"),
    OTPInput: true,
    headerClass: MODAL_HEADER_VARIANT.GREEN,
    primaryBtn: {
      label: tt("submit"),
      onClick: () => {
        verifyPin()
      },
    },
    secondaryBtn: {
      label: tt("close"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
    noHideOnSubmit: true,
    footerVariant: FooterVariant.TRANSACTIONAL,
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.PIN_VERIFYING, {
    percentage: 75,
    image: "password-user-80",
    text: tt("linkedin.global.status.pin-verifying.msg"),
    button: false,
    headerClass: MODAL_HEADER_VARIANT.BLUE,
    footerVariant: FooterVariant.ACKNOWLEDGMENT,
    primaryBtn: {
      label: tt("close-work-background"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.PIN_WRONG, {
    percentage: 75,
    image: "password-user-80",
    text: tt("linkedin.global.status.pin-wrong.msg"),
    input: false,
    primaryBtn: {
      label: tt("resend"),
      onClick: (person, userMessage, accountStatuses) => {
        clearModalReduxFields()
        labelStatusModal({ tt, person, statusName: "WRONG_AUTH", userMessage, accountStatuses })
      },
      bind: true,
    },
    secondaryBtn: {
      label: tt("close"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
    noHideOnSubmit: true,
    progressBar: LINKEDIN_ACCOUNT_GLOBAL_STATUS.TIMES_UP_CUSTOM_STATUS,
    headerClass: MODAL_HEADER_VARIANT.RED,
    footerVariant: FooterVariant.TRANSACTIONAL,
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.RESEND_PIN_CUSTOM_STATUS, {
    percentage: 75,
    image: false,
    text: tt("linkedin.global.status.resend-pin-custom.msg"),
    button: false,
    input: true,
    inputFiled: {
      placeholder: tt("enter-your-pin"),
      field: "pin",
      action: () => verifyPin(),
      text: tt("submit"),
    },
    progressBar: LINKEDIN_ACCOUNT_GLOBAL_STATUS.RESEND_PIN_CUSTOM_STATUS,
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.TIMES_UP_CUSTOM_STATUS, {
    percentage: 75,
    image: "password-user-80",
    text: tt("linkedin.global.status.time-up-pin-custom.msg"),
    input: false,
    primaryBtn: {
      label: tt("resend"),
      onClick: (person, userMessage, accountStatuses) => {
        clearModalReduxFields()
        labelStatusModal({ tt, person, statusName: "WRONG_AUTH", userMessage, accountStatuses })
      },
      bind: true,
    },
    secondaryBtn: {
      label: tt("close"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
    noHideOnSubmit: true,
    progressBar: LINKEDIN_ACCOUNT_GLOBAL_STATUS.TIMES_UP_CUSTOM_STATUS,
    headerClass: MODAL_HEADER_VARIANT.RED,
    footerVariant: FooterVariant.TRANSACTIONAL,
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.WRONG_AUTH, {
    percentage: 75,
    image: "input-check-80",
    text: tt("linkedin.global.status.wrong-auth.msg"),
    input: false,
    inputFiled: false,
    headerClass: MODAL_HEADER_VARIANT.RED,
    footerVariant: FooterVariant.ACKNOWLEDGMENT,
    primaryBtn: {
      label: tt("close"),
      onClick: (person, userMessage, accountStatuses) => {
        clearModalReduxFields()
        labelStatusModal({ tt, person, statusName: "WRONG_AUTH", userMessage, accountStatuses })
      },
      bind: true,
    },
    noHideOnSubmit: true,
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.TWO_FACTOR_AUTH, {
    percentage: 75,
    image: "password-mobile-phone-80",
    text: (
      <div>
        <p>{tt("linkedin.global.status.two-fa-auth.msg1")}</p>
        <p>{tt("linkedin.global.status.two-fa-auth.msg2")}</p>
      </div>
    ),
    primaryBtn: {
      label: tt("submit"),
      onClick: () => verifyTwoFactorAuth(),
    },
    secondaryBtn: {
      label: tt("close"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
    OTPInput: true,
    inputFiled: false,
    headerClass: MODAL_HEADER_VARIANT.GREEN,
    footerVariant: FooterVariant.TRANSACTIONAL,
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.TWO_FACTOR_AUTH_WRONG, {
    percentage: 75,
    image: "password-user-80",
    text: tt("linkedin.global.two-fa-auth-wrong.msg"),
    loginWithStream: {
      text: tt("try-via-linkedin"),
    },
    primaryBtn: {
      label: tt("re-enter-code"),
      onClick: (person, userMessage, accountStatuses) => {
        clearModalReduxFields()
        labelStatusModal({ tt, person, statusName: "WRONG_AUTH", userMessage, accountStatuses })
      },
      bind: true,
    },
    secondaryBtn: {
      onClick: () => {
        clearModalReduxFields()
        loginWithStream(tt)
      },
      label: tt("try-on-linkedin"),
      variant: ButtonVariant.SECONDARY,
    },
    tertiaryBtn: {
      label: tt("close"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
    noHideOnSubmit: true,
    input: false,
    inputFiled: false,
    headerClass: MODAL_HEADER_VARIANT.RED,
    footerVariant: FooterVariant.PROGRESS,
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.TWO_FACTOR_AUTH_ERROR, {
    percentage: 75,
    image: "password-user-80",
    text: tt("linkedin.global.status.two-fa-auth-error.msg"),
    loginWithStream: {
      text: tt("try-via-linkedin"),
    },
    primaryBtn: {
      label: tt("re-enter-code"),
      onClick: (person, userMessage, accountStatuses) => {
        clearModalReduxFields()
        labelStatusModal({ tt, person, statusName: "WRONG_AUTH", userMessage, accountStatuses })
      },
      bind: true,
    },
    secondaryBtn: {
      onClick: () => {
        clearModalReduxFields()
        loginWithStream(tt)
      },
      label: tt("try-on-linkedin"),
      variant: ButtonVariant.SECONDARY,
    },
    tertiaryBtn: {
      label: tt("close"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
    noHideOnSubmit: true,
    input: false,
    inputFiled: false,
    headerClass: MODAL_HEADER_VARIANT.RED,
    footerVariant: FooterVariant.PROGRESS,
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.TWO_FACTOR_AUTH_QUEUED, {
    percentage: 75,
    image: "password-user-80",
    text: tt("linkedin.global.status.two-fa-auth-queue.msg"),
    button: false,
    headerClass: MODAL_HEADER_VARIANT.BLUE,
    footerVariant: FooterVariant.ACKNOWLEDGMENT,
    primaryBtn: {
      label: tt("close-work-background"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.TWO_FACTOR_AUTH_VERIFYING, {
    percentage: 75,
    image: "password-user-80",
    text: tt("linkedin.global.status.two-fa-auth-queue.msg"),
    button: false,
    headerClass: MODAL_HEADER_VARIANT.BLUE,
    footerVariant: FooterVariant.ACKNOWLEDGMENT,
    primaryBtn: {
      label: tt("close-work-background"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.LOGIN_FAILED, {
    image: "circle-half-diagonal-80",
    text: tt("linkedin.global.status.login-fail.msg"),
    loginWithStream: {
      text: tt("try-by-myself"),
    },
    input: false,
    inputFiled: false,
    headerClass: MODAL_HEADER_VARIANT.RED,
    footerVariant: FooterVariant.TRANSACTIONAL,
    primaryBtn: {
      onClick: () => {
        clearModalReduxFields()
        loginWithStream(tt)
      },
      label: tt("try-on-linkedin"),
    },
    noHideOnSubmit: true,
    secondaryBtn: {
      label: tt("close"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.VERIFY_IDENTITY, {
    percentage: 75,
    image: "user-scan-80",
    text: tt("linkedin.global.status.verify-identity.msg"),
    input: false,
    inputFiled: false,
    headerClass: MODAL_HEADER_VARIANT.RED,
    footerVariant: FooterVariant.ACKNOWLEDGMENT,
    primaryBtn: {
      label: tt("close"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.SET_UP_TWO_FACTOR_AUTH, {
    percentage: 75,
    image: "password-mobile-phone-80",
    text: tt("linkedin.global.status.two-fa-auth-setup.msg"),
    button: false,
    input: false,
    inputFiled: false,
    headerClass: MODAL_HEADER_VARIANT.RED,
    footerVariant: FooterVariant.TRANSACTIONAL,
    primaryBtn: {
      label: tt("log-in-again"),
      onClick: () => {
        clearModalReduxFields()
        loginWithStream(tt)
      },
    },
    secondaryBtn: {
      label: "Cancel",
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
  })

  linkedinAccountGlobalStatus.set(LINKEDIN_ACCOUNT_GLOBAL_STATUS.REGISTER_PHONE, {
    percentage: 75,
    image: "phone-80",
    text: tt("linkedin.global.status.register-phone.msg"),
    input: false,
    inputFiled: false,
    headerClass: MODAL_HEADER_VARIANT.GREEN,
    footerVariant: FooterVariant.TRANSACTIONAL,
    primaryBtn: {
      onClick: () => {
        clearModalReduxFields()
        loginWithStream(tt)
      },
      label: tt("try-on-linkedin"),
    },
    secondaryBtn: {
      label: tt("close"),
      onClick: () => {
        clearModalReduxFields()
        store.dispatch(actions.closeModal())
      },
    },
    noHideOnSubmit: true,
  })

  return linkedinAccountGlobalStatus.get(id) || {}
}

export const ALLOWED_STATUSES = ["AUTH_OK", "OFFLINE"]
